import logger from '@ayocore/platform/common/helpers/logger';
import {
  validateNavigationItem,
  type NavigationItem,
  type NavigationLayout,
  type NavigationGroupRef,
  DEFAULT_NAVIGATION_WEIGHT,
} from '@ayocore/platform/client/types/navigation-item.type';
import { SidePanelController } from '@ayocore/platform/client/layouts/side_panel/SidePanelController';
import type { ClientSetupFn } from '@ayocore/platform/client/defineApp';
import { createElement, Fragment, lazy, type ComponentType, type ElementType } from 'react';

/**
 * Passthrough layout для WelcomePage — определён здесь, а не импортирован
 * из container-layouts.tsx, чтобы не тянуть BaseLayout.module.css
 * раньше Mantine стилей (иначе ломается CSS каскад glassmorphism).
 */
const WelcomePassthroughLayout: NavigationLayout = ({ children }) =>
  createElement(Fragment, null, children);

/** Дефолтный WelcomePage из платформы (lazy, создаётся один раз). */
const DefaultWelcomePage = lazy(
  () => import('@ayocore/platform/client/components/Welcome/WelcomePage')
);

/** Item с метаданными модуля для сортировки в меню. */
export type EnrichedNavigationItem = NavigationItem & {
  moduleNavigationOrder: number;
};

/**
 * AppRuntime
 *
 * Системный рантайм-агрегатор клиентской навигации модулей.
 *
 * Назначение:
 * - Принимает явный список setup-функций из <instance>/ayo.client.ts;
 * - Вызывает каждую с this — модули регистрируют навигацию, dock, public-routes;
 * - Валидирует элементы навигации через TypeBox-валидатор `validateNavigationItem`.
 */
export class AppRuntime {
  private static instance: AppRuntime;
  private initialized = false;
  private navigationItems: EnrichedNavigationItem[] = [];
  /** Глобальный реестр пунктов контекстного меню (например, для кнопки "+" у табов). */
  private contextMenuItems: Map<string, RegisteredContextMenuItem> = new Map();
  /** Единый index-route ("/"), который может зарегистрировать только один модуль. */
  private indexRoute: RegisteredIndexRoute | null = null;
  /** Публичные маршруты — вне BaseLayout/AuthGuard. Доступны без аутентификации. */
  private publicRoutes: PublicRouteDefinition[] = [];
  // Реестр клиентских модулей с метаданными и полной навигацией
  private modules: Map<string, ModuleRecord> = new Map();
  /**
   * Группы навигации по их id (`<модуль>:<ключ>`).
   *
   * Нужен, чтобы чужой модуль мог повесить свой пункт в готовую группу —
   * например, инстанс добавляет свой отчёт в раздел CRM. Без реестра каждому
   * модулю пришлось бы самому хранить и публиковать ссылку на свою группу.
   */
  private navigationGroups: Map<string, NavigationGroupRef> = new Map();
  private readonly sidePanelController = SidePanelController.getInstance();
  /** Слушатели изменений навигации (например, MenuController для перестройки меню). */
  private navigationListeners = new Set<() => void>();

  /**
   * Получить синглтон AppRuntime
   */
  static getInstance(): AppRuntime {
    if (!AppRuntime.instance) {
      AppRuntime.instance = new AppRuntime();
    }
    return AppRuntime.instance;
  }

  /**
   * Инициализация рантайма: вызов клиентских bootstrap-ов из ayo.client.ts инстанса.
   * Список setup-функций приходит явно — main.tsx импортирует ayo.client.ts и
   * передаёт `config.modules` сюда. Никакого авто-discovery.
   * Многократные вызовы безопасны — повторная инициализация не выполняется.
   */
  async init(modules: ClientSetupFn[]): Promise<void> {
    if (this.initialized) return;
    try {
      await this.loadModuleBootstraps(modules);
      // Навигация регистрируется через useNavigation() из bootstrap-модулей
      this.initialized = true;
      logger.info(`🧭 [AppRuntime] Initialized. Navigation items: ${this.navigationItems.length}`);
    } catch (error) {
      logger.error('❌ [AppRuntime] Failed to initialize:', error);
      // Даже при ошибке не помечаем как initialized, чтобы можно было повторить
    }
  }

  /**
   * Получить текущие элементы навигации (снапшот массива).
   * Каждый item содержит moduleNavigationOrder для сортировки в меню.
   */
  getNavigationItems(): EnrichedNavigationItem[] {
    return [...this.navigationItems];
  }

  /**
   * Подписка на изменения навигации. Уведомление приходит при каждой
   * дорегистрации пунктов через useNavigation() — в том числе после старта
   * приложения (динамические пункты меню). Возвращает функцию отписки.
   */
  subscribeNavigationChanged(listener: () => void): () => void {
    this.navigationListeners.add(listener);
    return () => this.navigationListeners.delete(listener);
  }

  /** Уведомляет слушателей об изменении навигации. */
  private emitNavigationChanged(): void {
    for (const listener of this.navigationListeners) {
      try {
        listener();
      } catch (error) {
        logger.error('❌ [AppRuntime] navigationChanged listener failed:', error);
      }
    }
  }

  /**
   * Зарегистрировать модуль и получить API для регистрации фич модуля.
   * Аналог сервера: app.useModule(name, meta) → module.useNavigation(...)
   *
   * @param meta.navigationOrder — порядок модуля в навигации (меньше — выше). По умолчанию 9999999 (в конец).
   */
  useModule(
    name: string,
    meta?: { version?: string; description?: string; navigationOrder?: number }
  ) {
    // Валидация имени модуля: только строчные латинские буквы, цифры и "_";
    // первая буква обязательно латинская строчная.
    this.validateModuleName(name);

    // Регистрируем/обновляем запись модуля
    const existing = this.modules.get(name);
    const record: ModuleRecord = {
      version: meta?.version ?? existing?.version,
      description: meta?.description ?? existing?.description,
      basePath: existing?.basePath ?? `/${name}`,
      navigationAll: existing?.navigationAll ?? [],
      navigationOrder:
        meta?.navigationOrder ?? existing?.navigationOrder ?? DEFAULT_NAVIGATION_WEIGHT,
    };
    this.modules.set(name, record);

    // An empty list also notifies subscribers: dynamic guards may have changed
    // even when there are no new items to register (e.g. the last project was deleted).
    const useNavigation = (items: NavigationItem | NavigationItem[]) => {
      this.addNavigation(items, name);
    };
    const useDock = (items: DockItemDefinition | DockItemDefinition[]) => {
      this.addDockItems(items, name);
    };
    const registerContextMenu = (
      items: ContextMenuItemDefinition | ContextMenuItemDefinition[]
    ) => {
      this.addContextMenuItems(items, name);
    };
    const useIndexRoute = (route: IndexRouteDefinition) => {
      this.registerIndexRoute(route, name);
    };

    const usePublicRoute = (route: PublicRouteDefinition) => {
      this.registerPublicRoute(route);
    };

    const createNavigationGroup = (props: {
      /** Уникальный ключ группы: латиница, цифры, _, -. Без пробелов. */
      key: string;
      label: string;
      icon: ComponentType<{ size?: number; className?: string; stroke?: number }>;
      order: number;
    }): NavigationGroupRef => {
      this.validateGroupKey(props.key);
      const id = `${name}:${props.key}`;
      const group: NavigationGroupRef = {
        id,
        label: props.label,
        icon: props.icon,
        order: props.order,
      };
      // Кладём в реестр: по этой ссылке чужие модули вешают свои пункты.
      this.navigationGroups.set(id, group);
      return group;
    };

    return {
      useNavigation,
      useDock,
      registerContextMenu,
      useIndexRoute,
      createNavigationGroup,
      usePublicRoute,
    } as const;
  }

  /**
   * Ссылка на группу навигации чужого модуля — чтобы повесить в неё свой пункт.
   *
   * Типичный случай: инстанс добавляет собственный раздел в меню модуля, не
   * трогая его код:
   *
   * ```ts
   * grasias.useNavigation([
   *   { group: app.getNavigationGroup('crm', 'crm'), label: 'Анализ платных макетов', ... },
   * ]);
   * ```
   *
   * Владелец группы обычно экспортирует тонкий хелпер, чтобы потребитель не
   * писал имя модуля строкой — тогда переименование модуля не разъедет меню.
   *
   * Бросает, а не возвращает undefined: пункт с несуществующей группой ушёл бы
   * в меню молча и не там, где ждали, — искать такое потом дорого.
   */
  getNavigationGroup(moduleName: string, key: string): NavigationGroupRef {
    const id = `${moduleName}:${key}`;
    const group = this.navigationGroups.get(id);
    if (!group) {
      const known = [...this.navigationGroups.keys()].join(', ') || 'ни одной';
      throw new Error(
        `[AppRuntime] Группа навигации "${id}" не найдена. ` +
          `Либо модуль "${moduleName}" ещё не поднялся (его bootstrap должен идти раньше), ` +
          `либо ключ другой. Известные группы: ${known}`
      );
    }
    return group;
  }

  /**
   * Возвращает доступные пункты контекстного меню.
   * Перед выдачей применяет guard-фильтрацию и сортировку по order.
   */
  getContextMenuItems(): ContextMenuItem[] {
    const result: ContextMenuItem[] = [];

    for (const item of this.contextMenuItems.values()) {
      if (item.guard) {
        try {
          if (!item.guard()) {
            continue;
          }
        } catch (error) {
          logger.warn(`[AppRuntime] Context menu guard threw for "${item.id}"`, error);
          continue;
        }
      }

      result.push({
        id: item.id,
        title: item.title,
        icon: item.icon,
        onSelect: item.onSelect,
        guard: item.guard,
        order: item.order,
      });
    }

    return result.sort((left, right) => {
      if (left.order !== right.order) return left.order - right.order;
      return left.title.localeCompare(right.title);
    });
  }

  /**
   * Регистрирует публичный маршрут — вне BaseLayout/AuthGuard.
   * Доступен без аутентификации. Например: /oauth/login, /oauth-invite.
   */
  private registerPublicRoute(route: PublicRouteDefinition): void {
    if (!route?.path || !route?.component) {
      throw new Error(`InvalidPublicRoute: path and component are required`);
    }
    this.publicRoutes.push(route);
  }

  /**
   * Возвращает все публичные маршруты для Router.tsx.
   * Рендерятся вне BaseLayout/AuthGuard.
   */
  getPublicRoutes(): PublicRouteDefinition[] {
    return [...this.publicRoutes];
  }

  /**
   * Регистрирует index-route для "/" от конкретного модуля.
   * Повторная регистрация (даже тем же модулем) запрещена и приводит к throw.
   */
  private registerIndexRoute(route: IndexRouteDefinition, moduleName: string): void {
    if (!route?.component) {
      throw new Error(`InvalidIndexRoute: route.component обязателен. module="${moduleName}"`);
    }

    if (this.indexRoute) {
      throw new Error(
        `IndexRouteAlreadyRegistered: "/" уже зарегистрирован модулем "${this.indexRoute.moduleName}", повторная регистрация из "${moduleName}" запрещена.`
      );
    }

    this.indexRoute = {
      ...route,
      moduleName,
    };
  }

  /**
   * Возвращает зарегистрированный index-route ("/").
   * Если ни один модуль не зарегистрировал — возвращает дефолтный WelcomePage из платформы.
   */
  getIndexRoute(): IndexRouteDefinition {
    if (this.indexRoute) {
      return {
        component: this.indexRoute.component,
        layout: this.indexRoute.layout,
      };
    }

    return {
      component: DefaultWelcomePage,
      layout: WelcomePassthroughLayout,
    };
  }

  /** Проверяет key группы: только a-z, 0-9, _, -. Без пробелов. Бросает при несоответствии. */
  private validateGroupKey(key: string): void {
    const pattern = /^[a-z0-9_-]+$/;
    if (!pattern.test(key)) {
      throw new Error(
        `InvalidGroupKey: ключ группы должен соответствовать /^[a-z0-9_-]+$/; получено: "${key}"`
      );
    }
  }

  /**
   * Внутренняя регистрация элементов навигации от конкретного модуля.
   * Источник истины «какие модули включены» теперь — список в ayo.client.ts.
   * Если модуль не должен быть в инстансе, он просто не импортируется в конфиг —
   * сюда не доходит. Поэтому фильтрация по env-переменной убрана.
   */
  private addNavigation(items: NavigationItem | NavigationItem[], moduleName: string): void {
    const toArray = Array.isArray(items) ? items : [items];

    const validItems: EnrichedNavigationItem[] = [];
    let changed = toArray.length === 0;

    for (const item of toArray) {
      const { valid, errors } = validateNavigationItem(item);
      if (!valid) {
        logger.error(
          `❌ [AppRuntime] Invalid navigation item: ${JSON.stringify(item)} Errors: ${(errors || []).join('; ')}`
        );
        continue;
      }

      // Валидация guard-функции: если guard есть, он должен быть функцией
      if (item.guard && typeof item.guard !== 'function') {
        logger.warn(`[AppRuntime] Navigation guard is not a function for ${item.url}, skipping`);
        continue;
      }

      // Сохраняем ВСЕ элементы в запись модуля для роутинга
      const moduleRecord: ModuleRecord = this.modules.get(moduleName) ?? {
        version: undefined,
        description: undefined,
        basePath: `/${moduleName}`,
        navigationAll: [],
        navigationOrder: DEFAULT_NAVIGATION_WEIGHT,
      };
      const byUrlAll = new Map(
        moduleRecord.navigationAll.map((i: NavigationItem) => [i.url, i] as const)
      );
      byUrlAll.set(item.url, item);
      moduleRecord.navigationAll = Array.from(byUrlAll.values());
      this.modules.set(moduleName, moduleRecord);
      changed = true;

      // Для меню скрытые элементы пропускаем
      if (item.hiddenFromMenu) continue;

      const moduleOrder = moduleRecord.navigationOrder ?? DEFAULT_NAVIGATION_WEIGHT;
      validItems.push({ ...item, moduleNavigationOrder: moduleOrder });
    }

    // Простой мёрдж без дубликатов по url
    const existingByUrl = new Map(this.navigationItems.map(i => [i.url, i] as const));
    for (const i of validItems) {
      existingByUrl.set(i.url, i);
    }
    this.navigationItems = Array.from(existingByUrl.values());

    if (changed) {
      this.emitNavigationChanged();
    }
  }

  /**
   * Добавляет док-элементы для боковой панели от конкретного модуля.
   * Идентификаторы элементов автоматически неймспейсятся именем модуля.
   */
  private addDockItems(items: DockItemDefinition | DockItemDefinition[], moduleName: string): void {
    const toArray = Array.isArray(items) ? items : [items];

    for (const item of toArray) {
      if (!item || typeof item !== 'object') {
        logger.error(`❌ [AppRuntime] Dock item is not an object: ${JSON.stringify(item)}`);
        continue;
      }

      if (!item.id || !item.label || !item.icon || !item.component) {
        logger.error(`❌ [AppRuntime] Dock item has missing fields: ${JSON.stringify(item)}`);
        continue;
      }

      const namespacedId = `${moduleName}:${item.id}`;

      this.sidePanelController.registerDockItem({
        id: namespacedId,
        label: item.label,
        icon: item.icon,
        component: item.component,
        badge: item.badge,
        order: item.order,
        mode: item.mode,
      });
    }
  }

  /**
   * Добавляет пункты контекстного меню от конкретного модуля.
   * Идентификаторы автоматически неймспейсятся именем модуля.
   */
  private addContextMenuItems(
    items: ContextMenuItemDefinition | ContextMenuItemDefinition[],
    moduleName: string
  ): void {
    const toArray = Array.isArray(items) ? items : [items];

    for (const item of toArray) {
      if (!item || typeof item !== 'object') {
        logger.error(`❌ [AppRuntime] Context menu item is not an object: ${JSON.stringify(item)}`);
        continue;
      }

      if (!item.id || !item.title || !item.icon || !item.onSelect) {
        logger.error(
          `❌ [AppRuntime] Context menu item has missing fields: ${JSON.stringify(item)}`
        );
        continue;
      }

      if (typeof item.onSelect !== 'function') {
        logger.error(
          `❌ [AppRuntime] Context menu onSelect is not a function: ${JSON.stringify(item)}`
        );
        continue;
      }

      if (item.guard && typeof item.guard !== 'function') {
        logger.error(
          `❌ [AppRuntime] Context menu guard is not a function: ${JSON.stringify(item)}`
        );
        continue;
      }

      const namespacedId = `${moduleName}:${item.id}`;
      const normalizedOrder = Number.isFinite(item.order)
        ? (item.order as number)
        : Number.MAX_SAFE_INTEGER;

      this.contextMenuItems.set(namespacedId, {
        id: namespacedId,
        title: item.title,
        icon: item.icon,
        onSelect: item.onSelect,
        guard: item.guard,
        order: normalizedOrder,
      });
    }
  }

  /**
   * Возвращает конфигурацию роутинга, сгруппированную по модулям.
   * Каждый модуль содержит базовый путь и относительные пути для своих страниц.
   */
  getRouteConfig(): Array<{
    moduleName: string;
    basePath: string;
    children: Array<{
      path: string;
      component: ComponentType<unknown>;
      url: string;
      label: string;
      layout?: NavigationLayout;
    }>;
  }> {
    const result: Array<{
      moduleName: string;
      basePath: string;
      children: Array<{
        path: string;
        component: ComponentType<unknown>;
        url: string;
        label: string;
        layout?: NavigationLayout;
      }>;
    }> = [];
    logger.info('🧭 [AppRuntime] getRouteConfig', { modules: Array.from(this.modules.keys()) });

    for (const [moduleName, record] of this.modules.entries()) {
      const basePath = record.basePath || `/${moduleName}`;
      const children = (record.navigationAll || [])
        .filter((item: NavigationItem) => Boolean(item.component))
        .map(item => {
          const url = item.url || '';
          let relative = '';
          if (url.startsWith(basePath)) {
            relative = url.slice(basePath.length);
          } else if (url.startsWith('/' + moduleName)) {
            // Защита от исторических URL, если basePath менялся
            relative = url.slice(moduleName.length + 1);
          } else {
            relative = '';
          }
          const cleanPath = relative.startsWith('/') ? relative.slice(1) : relative;
          return {
            path: cleanPath || '',
            component: item.component as ComponentType<unknown>,
            url: item.url,
            label: item.label,
            layout: item.layout,
          };
        });

      result.push({ moduleName, basePath, children });
    }

    return result;
  }

  /**
   * Запуск клиентских bootstrap-ов из явного списка (ayo.client.ts инстанса).
   * Каждый setup получает this и регистрирует своё (навигация, dock-items,
   * context-menu, public-routes, ...). Порядок — как в массиве конфига.
   */
  private async loadModuleBootstraps(modules: ClientSetupFn[]): Promise<void> {
    for (let i = 0; i < modules.length; i++) {
      const setup = modules[i];
      try {
        await setup(this);
      } catch (error) {
        logger.error(`❌ [AppRuntime] Bootstrap setup failed at index ${i}:`, error);
      }
    }
  }

  /**
   * Проверяет корректность имени модуля.
   * Требования: начинается с латинской строчной буквы; далее строчные буквы, цифры, подчёркивание или тире.
   * Пример допустимого имени: "notifications", "crm-v2", "schema_admin1".
   * Бросает ошибку при несоответствии.
   */
  private validateModuleName(name: string): void {
    const pattern = /^[a-z][a-z0-9_-]*$/;
    if (!pattern.test(name)) {
      throw new Error(
        `InvalidModuleName: имя модуля должно соответствовать /^[a-z][a-z0-9_-]*$/; получено: "${name}"`
      );
    }
  }
}

/**
 * Внутренняя структура записи модуля в клиентском рантайме.
 */
interface ModuleRecord {
  version?: string;
  description?: string;
  basePath: string;
  navigationAll: NavigationItem[];
  /** Порядок модуля в навигации. По умолчанию DEFAULT_NAVIGATION_WEIGHT. */
  navigationOrder: number;
}

/**
 * Контракт регистрации index-route ("/") в клиентском модуле.
 */
export interface IndexRouteDefinition {
  component: ComponentType<unknown>;
  layout?: NavigationLayout;
}

/**
 * Публичный маршрут — рендерится вне BaseLayout/AuthGuard.
 * Доступен без аутентификации (например: страница OAuth-логина, инвайт).
 */
export interface PublicRouteDefinition {
  /** Абсолютный путь, например '/oauth/login' или '/oauth-invite'. */
  path: string;
  component: ComponentType<unknown>;
}

interface RegisteredIndexRoute extends IndexRouteDefinition {
  moduleName: string;
}

/**
 * Контракт регистрации док-элемента из клиентского модуля.
 */
export interface DockItemDefinition {
  id: string;
  label: string;
  icon: ElementType;
  component: ComponentType;
  badge?: ComponentType;
  order?: number;
  /** Режим отображения: 'panel' — боковая панель, 'fullscreen' — на весь экран поверх контента */
  mode?: 'panel' | 'fullscreen';
}

/**
 * Произвольный payload для callback контекстного меню.
 * В текущем сценарии может быть не передан.
 */
export type ContextMenuPayload = Record<string, unknown>;

/**
 * Контракт регистрации пункта контекстного меню из клиентского модуля.
 */
export interface ContextMenuItemDefinition {
  id: string;
  title: string;
  icon: ComponentType<{ size?: number; className?: string; stroke?: number }>;
  onSelect: (payload?: ContextMenuPayload) => void | Promise<void>;
  guard?: () => boolean;
  order?: number;
}

/**
 * Публичный элемент контекстного меню в рантайме (уже с неймспейсным id).
 */
export interface ContextMenuItem {
  id: string;
  title: string;
  icon: ComponentType<{ size?: number; className?: string; stroke?: number }>;
  onSelect: (payload?: ContextMenuPayload) => void | Promise<void>;
  guard?: () => boolean;
  order: number;
}

type RegisteredContextMenuItem = ContextMenuItem;

export default AppRuntime;
